import { useMemo, useCallback, type ReactNode } from "react";
import { Button } from "web-design-system";
import { useQuery } from "@tanstack/react-query";
import {
  investorProfileQueryOptions,
  useClient,
  useInvalidateChangeSet,
  useInvestorBankAccounts,
  usePaymentLink,
  useRecentlyFailedOrders,
  useRejectChangeRequest,
  useSendRiskProfileQuestionnare,
  useSubmittedOrders,
} from "creso-core/hooks";
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { handleError } from "@/utils/error";
import {
  JOINT_CLIENT_HOLDING_PATTERNS,
  NON_GIFT_PRODUCTS,
} from "creso-core/constants";
import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  addFullStopToSentence,
  convertToCurrency,
  findPrimaryHolder,
  findSecondHolder,
  isSecondHolderDetailsIncomplete,
} from "creso-core/utils";
import { Client } from "creso-core/types";
import ThrottledButton from "@core/components/throttledButton";
import { AmcLogoGroup } from "web-core";
import {
  ClientStatusLayout,
  clientStatusContentClassName,
  type ClientStatusMemberDisplay,
} from "./client-status-layout";
import { useKycBanners } from "./use-kyc-banners";

// Cards as data, not a self-framing component — the dispatcher collects
// cards from every branch and is the only place that knows the real count.
export function useActiveStatusCards(
  client: Client,
  memberDisplay: ClientStatusMemberDisplay | undefined,
  enabled: boolean
): ReactNode[] {
  const router = useRouter();
  const guid = enabled ? (client.investment_account ?? "") : "";
  const isJointAccount = Boolean(
    client.holding_pattern &&
    JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
  );

  const { data: holderProfiles, isPending: isHolderProfilesPending } = useQuery(
    investorProfileQueryOptions(
      { client_id: client.id },
      { enabled: enabled && isJointAccount }
    )
  );

  const { refetch: fetchClient, isFetching: isFetchingClient } = useClient(
    client.id,
    false
  );

  const kycBanner = useKycBanners(client);

  const { data: bankAccounts } = useInvestorBankAccounts(guid);

  const bankAccountWithFailedVerification = useMemo(
    () => bankAccounts?.find((b) => b.verification_status === "failed"),
    [bankAccounts]
  );

  const { data: ordersResult } = useSubmittedOrders(
    {
      investment_account_guid: guid,
      page: 1,
      pageSize: 100,
      status: "payment_pending",
      productType: NON_GIFT_PRODUCTS.join(","),
    },
    { enabled }
  );

  const [recentlyFailedOrders, retry] = useRecentlyFailedOrders(guid);

  const handleRetryOrders = useCallback(async () => {
    try {
      const [failed, success] = await retry();
      if (failed.length) {
        toast.error("Failed to add orders to cart", {
          description: failed.map((o) => o && JSON.stringify(o)).join(", "),
        });
      }
      if (success.length) {
        router.navigate({
          to: ".",
          search: (prev) => ({
            ...prev,
            modal: "transaction",
            client_id: client.id,
          }),
        });
      }
    } catch {
      toast.error("Failed to add orders to cart");
    }
  }, [retry, router, client.id]);

  const { mutateAsync: generatePaymentLink } = usePaymentLink(guid);

  const { mutateAsync: discardChanges } = useRejectChangeRequest();

  const invalidateChangeSet = useInvalidateChangeSet(client.id);

  const { mutateAsync: sendRiskProfileLink } = useSendRiskProfileQuestionnare();

  const onShareRiskProfileLink = useCallback(async () => {
    try {
      await sendRiskProfileLink(client.id);
      toast.success("Risk profile link sent to the client");
    } catch (e) {
      handleError(e, "Failed to send link.");
    }
  }, [client?.id, sendRiskProfileLink]);

  const onAssessRiskProfile = useCallback(() => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        modal: "risk-profile",
        client_id: client.id,
      }),
    });
  }, [router, client.id]);

  const steps = useMemo(() => {
    if (!enabled) return [];

    const steps = [];

    if (kycBanner) {
      steps.push({
        id: "KYC",
        title: () => <span className="text-sm">{kycBanner.description}</span>,
        action: () => kycBanner.actions,
      });
    }

    const secondHolderDetailsIncomplete =
      isJointAccount &&
      !isHolderProfilesPending &&
      isSecondHolderDetailsIncomplete(holderProfiles) &&
      client.status === "AUTHENTICATION_PENDING";

    if (secondHolderDetailsIncomplete) {
      steps.push({
        id: "SECOND_HOLDER_DETAILS_MISSING",
        title: () => (
          <span className="text-sm">
            <span className="font-semibold">
              Second holder&apos;s details missing.
            </span>{" "}
            Continue to complete the missing details to notify holders for
            approval.
          </span>
        ),
        action: () => (
          <Button
            onClick={() =>
              router.navigate({
                to: ".",
                search: (prev) => ({
                  ...prev,
                  modal: "add-second-holder",
                  client_id: client.id,
                  context: undefined,
                }),
              })
            }
          >
            Continue
          </Button>
        ),
      });
    }

    const primaryHolder = findPrimaryHolder(holderProfiles);
    const secondHolder = findSecondHolder(holderProfiles);
    const showApprovalPendingBanner =
      isJointAccount &&
      !isHolderProfilesPending &&
      !secondHolderDetailsIncomplete &&
      !(primaryHolder?.is_approved && secondHolder?.is_approved) &&
      client.status === "AUTHENTICATION_PENDING";

    if (showApprovalPendingBanner) {
      steps.push({
        id: "SECOND_HOLDER_APPROVAL_PENDING",
        title: () => (
          <span className="text-sm">
            <span className="font-semibold">Approval pending.</span> Approval is
            required from both holders to finish joint account setup.
          </span>
        ),
        action: () => (
          <Button
            onClick={() =>
              router.navigate({
                to: ".",
                search: (prev) => ({
                  ...prev,
                  modal: "add-second-holder",
                  client_id: client.id,
                  context: "approval-status",
                }),
              })
            }
          >
            Notify
          </Button>
        ),
      });
    }

    if (bankAccountWithFailedVerification?.verification_status === "failed") {
      steps.push({
        id: "BANK_DETAILS_FAILED",
        title: () => (
          <span className="text-sm">
            <span className="font-semibold">
              Bank details verification failed:
            </span>{" "}
            {`${addFullStopToSentence(bankAccountWithFailedVerification?.verification_remark ?? "")} Resubmit new bank details from account center.`}
          </span>
        ),
        action: () => null,
      });
    }

    if (client.status === "UPDATE_PENDING") {
      steps.push({
        id: "UPDATE_PENDING",
        title: () => (
          <span className="text-sm">
            <span className="font-semibold">Account details edited:</span>{" "}
            {client.name} needs to authenticate the details from their app.
          </span>
        ),
        action: () => {
          return (
            <div className="flex flex-row gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  discardChanges(client.id).then(
                    async () => {
                      router.invalidate();
                      // router.invalidate() re-runs this route's loader, which on the
                      // client page also fetches the ["client", {id}] query below —
                      // refetch()'s default cancelRefetch:true would abort that
                      // in-flight loader fetch and throw a CancelledError inside
                      // TanStack Router itself (uncatchable from here), so opt out of
                      // cancelling and just await whichever fetch is already running.
                      await invalidateChangeSet().catch(() => {});
                      await fetchClient({ cancelRefetch: false }).catch(
                        () => {}
                      );
                      toast.success("Changes discarded successfully");
                    },
                    (err) => handleError(err, "Failed to discard changes")
                  )
                }
              >
                Discard
              </Button>
              <Button
                onClick={() =>
                  router.navigate({
                    to: ".",
                    search: (prev) => ({ ...prev, modal: "account-updates" }),
                  })
                }
              >
                Notify
              </Button>
            </div>
          );
        },
      });
    }

    if (client.status === "UPDATE_UNDER_REVIEW") {
      steps.push({
        id: "UPDATE_UNDER_REVIEW",
        title: () => (
          <span className="text-sm">
            <span className="font-semibold">Account details submitted:</span>{" "}
            Changes are being reviewed. It could take a few minutes.
          </span>
        ),
        action: () => (
          <Button
            disabled={isFetchingClient}
            onClick={() => {
              fetchClient()
                .then(({ data: client }) => {
                  if (client?.status === "ACTIVE") {
                    toast.success("Account changes updated successfully");
                    router.invalidate();
                  } else {
                    toast.info("Account changes are still under review");
                  }
                })
                .catch((e) => handleError(e, "Failed to check status"));
            }}
            variant="outline"
          >
            Check Status
          </Button>
        ),
      });
    }

    if (ordersResult?.results && ordersResult.results.length > 0) {
      const totalOrderValue =
        ordersResult?.results?.reduce(
          (acc, order) => acc + Number(order.amount),
          0
        ) || 0;
      steps.push({
        id: "PAYMENT_PENDING",
        title: () => (
          <div className="flex items-center">
            <AmcLogoGroup size="md" items={ordersResult.results} />
            <span className="text-sm ml-3">
              Payment of
              <span className="font-semibold">
                &nbsp;{convertToCurrency(totalOrderValue, 0)}&nbsp;
              </span>
              pending for {ordersResult.results.length} order(s)
            </span>
          </div>
        ),
        action: () => {
          return (
            <Button
              onClick={() =>
                generatePaymentLink(true)
                  .then(() => {
                    toast.success("Payment link sent successfully");
                  })
                  .catch((error) =>
                    handleError(error, "Failed to send payment link")
                  )
              }
            >
              Send link
            </Button>
          );
        },
      });
    }

    if (recentlyFailedOrders.length) {
      const logos = Array.from(
        new Set(recentlyFailedOrders.map((o) => o.amc_morningstar_code))
      ).slice(0, 3);
      steps.push({
        id: "RECENT_ORDER_FAILED",
        title: () => (
          <div className="flex items-center">
            {logos.map((l) => (
              <AmcLogo size="sm" key={l} amc_code={l} />
            ))}
            <span className="text-sm ml-3">
              <span className="font-semibold">Orders failed:&nbsp;</span>
              {recentlyFailedOrders.length} purchase order(s) failed recently.
              Add them back to the cart in one click.
            </span>
          </div>
        ),
        action: () => {
          return <Button onClick={handleRetryOrders}>Add to cart</Button>;
        },
      });
    }

    if (!client.risk_profile) {
      steps.push({
        id: "RISK_PROFILE_PENDING",
        title: () => (
          <span className="text-sm">
            <span className="font-semibold">Risk profile pending.&nbsp;</span>
            Assess the investor before recommending investments.
          </span>
        ),
        action: () => (
          <>
            <ThrottledButton
              beginOnMount={false}
              throttle={25}
              variant="outline"
              onClick={onShareRiskProfileLink}
            >
              Send Link
            </ThrottledButton>
            <Button onClick={onAssessRiskProfile}>Assess</Button>
          </>
        ),
      });
    }
    return steps;
  }, [
    enabled,
    kycBanner,
    client,
    router,
    ordersResult?.results,
    generatePaymentLink,
    isFetchingClient,
    fetchClient,
    bankAccountWithFailedVerification,
    discardChanges,
    invalidateChangeSet,
    recentlyFailedOrders,
    handleRetryOrders,
    onShareRiskProfileLink,
    onAssessRiskProfile,
    isJointAccount,
    isHolderProfilesPending,
    holderProfiles,
  ]);

  if (!enabled) return [];

  return steps.map((step) => (
    <ClientStatusLayout
      key={`${client.id}-${step.id}`}
      memberDisplay={memberDisplay}
    >
      <div
        className={clientStatusContentClassName(
          memberDisplay,
          "justify-between gap-2"
        )}
      >
        {step.title ? <step.title /> : null}
        <div className="ml-auto flex shrink-0 flex-row gap-2">
          {step.action ? <step.action /> : null}
        </div>
      </div>
    </ClientStatusLayout>
  ));
}
